/**
 * Grade Thresholds Tab Component
 * Handles supplier grade threshold configuration, editing, and management
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import {
  Loader2,
  RotateCcw,
  AlertTriangle,
  CheckCircle,
  Pencil,
  X,
  Check,
  Plus,
  Trash2,
} from 'lucide-react';
import {
  SupplierPerformanceSettings,
  SupplierRatingThreshold,
  CreateRatingThresholdData,
} from '../../../lib/api';
import { ThresholdEditData, GRADE_COLORS } from './types';

interface GradeThresholdsTabProps {
  settings: SupplierPerformanceSettings | null;
  editingThreshold: number | null;
  thresholdEditData: ThresholdEditData | null;
  savingThreshold: boolean;
  showAddThreshold: boolean;
  // CR-7271: the form does not carry `performance_settings`; the hook supplies
  // it from the settings row being edited at the point of the POST.
  newThresholdData: Omit<CreateRatingThresholdData, 'performance_settings'>;
  addingThreshold: boolean;
  onSetShowAddThreshold: (show: boolean) => void;
  onResetThresholds: () => void;
  onEditThreshold: (threshold: SupplierRatingThreshold) => void;
  onCancelEditThreshold: () => void;
  onSaveThreshold: (thresholdId: number) => void;
  onThresholdFieldChange: (field: string, value: string | number) => void;
  onAddThreshold: () => void;
  onDeleteThreshold: (thresholdId: number, label: string) => void;
  onNewThresholdFieldChange: (field: string, value: string | number) => void;
}

export function GradeThresholdsTab({
  settings,
  editingThreshold,
  thresholdEditData,
  savingThreshold,
  showAddThreshold,
  newThresholdData,
  addingThreshold,
  onSetShowAddThreshold,
  onResetThresholds,
  onEditThreshold,
  onCancelEditThreshold,
  onSaveThreshold,
  onThresholdFieldChange,
  onAddThreshold,
  onDeleteThreshold,
  onNewThresholdFieldChange,
}: GradeThresholdsTabProps) {
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Supplier Grade Thresholds</CardTitle>
              <CardDescription>
                Define the score ranges and custom labels for each supplier rating grade.
                Click on a grade to edit its settings. You can add custom grades.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => onSetShowAddThreshold(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Grade
              </Button>
              <Button variant="outline" size="sm" onClick={onResetThresholds}>
                <RotateCcw className="h-4 w-4 mr-2" />
                Reset to Defaults
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {settings?.rating_thresholds && settings.rating_thresholds.length > 0 ? (
            <div className="space-y-4">
              {settings.rating_thresholds.map((threshold) => (
                <div key={threshold.id}>
                  {editingThreshold === threshold.id && thresholdEditData ? (
                    // Edit Mode
                    <ThresholdEditForm
                      threshold={threshold}
                      thresholdEditData={thresholdEditData}
                      savingThreshold={savingThreshold}
                      onCancelEditThreshold={onCancelEditThreshold}
                      onSaveThreshold={onSaveThreshold}
                      onThresholdFieldChange={onThresholdFieldChange}
                      onDeleteThreshold={onDeleteThreshold}
                    />
                  ) : (
                    // View Mode
                    <ThresholdViewItem
                      threshold={threshold}
                      onEditThreshold={onEditThreshold}
                    />
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-4">
              No grade thresholds configured. Click "Reset to Defaults" to create default thresholds.
            </p>
          )}

          {/* Add New Grade Form */}
          {showAddThreshold && (
            <AddThresholdForm
              newThresholdData={newThresholdData}
              addingThreshold={addingThreshold}
              onSetShowAddThreshold={onSetShowAddThreshold}
              onAddThreshold={onAddThreshold}
              onNewThresholdFieldChange={onNewThresholdFieldChange}
            />
          )}

          {/* Grade Legend */}
          <div className="mt-6 p-4 bg-muted rounded-lg">
            <h4 className="font-medium mb-3">Grade Labels</h4>
            <p className="text-sm text-muted-foreground mb-3">
              Custom labels allow you to give meaningful names to each grade level (e.g., Gold, Silver, Bronze).
            </p>
            <div className="flex flex-wrap gap-2">
              {settings?.rating_thresholds?.map((threshold) => (
                <div key={threshold.id} className="flex items-center gap-2">
                  <div
                    className="w-6 h-6 rounded flex items-center justify-center text-white text-sm font-bold"
                    style={{ backgroundColor: threshold.effective_color || threshold.color }}
                  >
                    {threshold.grade}
                  </div>
                  <span className="text-sm">
                    {threshold.display_label || threshold.label || threshold.grade}
                  </span>
                </div>
              )) || ['A', 'B', 'C', 'D', 'F'].map((grade) => (
                <div key={grade} className="flex items-center gap-2">
                  <div
                    className="w-6 h-6 rounded flex items-center justify-center text-white text-sm font-bold"
                    style={{ backgroundColor: GRADE_COLORS[grade] }}
                  >
                    {grade}
                  </div>
                  <span className="text-sm">{grade}</span>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Threshold Edit Form Sub-component
interface ThresholdEditFormProps {
  threshold: SupplierRatingThreshold;
  thresholdEditData: ThresholdEditData;
  savingThreshold: boolean;
  onCancelEditThreshold: () => void;
  onSaveThreshold: (thresholdId: number) => void;
  onThresholdFieldChange: (field: string, value: string | number) => void;
  onDeleteThreshold: (thresholdId: number, label: string) => void;
}

function ThresholdEditForm({
  threshold,
  thresholdEditData,
  savingThreshold,
  onCancelEditThreshold,
  onSaveThreshold,
  onThresholdFieldChange,
  onDeleteThreshold,
}: ThresholdEditFormProps) {
  return (
    <div
      className="p-4 border rounded-lg space-y-4"
      style={{ borderLeftColor: thresholdEditData.color, borderLeftWidth: '4px' }}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className="w-12 h-12 rounded-lg flex items-center justify-center text-white text-xl font-bold"
            style={{ backgroundColor: thresholdEditData.color }}
          >
            {threshold.grade}
          </div>
          <div className="font-medium text-lg">Edit Grade {threshold.grade}</div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancelEditThreshold}
            disabled={savingThreshold}
          >
            <X className="h-4 w-4 mr-1" />
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={() => onSaveThreshold(threshold.id)}
            disabled={savingThreshold}
          >
            {savingThreshold ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Check className="h-4 w-4 mr-1" />
            )}
            Save
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <div className="space-y-2">
          <Label htmlFor={`grade-${threshold.id}`}>Grade ID</Label>
          <Input
            id={`grade-${threshold.id}`}
            value={thresholdEditData.grade}
            onChange={(e) => onThresholdFieldChange('grade', e.target.value)}
            placeholder="e.g., A, B, C"
            maxLength={10}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor={`label-${threshold.id}`}>Display Label</Label>
          <Input
            id={`label-${threshold.id}`}
            value={thresholdEditData.label}
            onChange={(e) => onThresholdFieldChange('label', e.target.value)}
            placeholder="e.g., Gold, Silver"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor={`min-score-${threshold.id}`}>Min Score</Label>
          <Input
            id={`min-score-${threshold.id}`}
            type="number"
            min="0"
            max="100"
            value={thresholdEditData.min_score}
            onChange={(e) => onThresholdFieldChange('min_score', e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor={`max-score-${threshold.id}`}>Max Score</Label>
          <Input
            id={`max-score-${threshold.id}`}
            type="number"
            min="0"
            max="100"
            value={thresholdEditData.max_score}
            onChange={(e) => onThresholdFieldChange('max_score', e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor={`color-${threshold.id}`}>Color</Label>
          <div className="flex items-center gap-2">
            <input
              id={`color-${threshold.id}`}
              type="color"
              value={thresholdEditData.color}
              onChange={(e) => onThresholdFieldChange('color', e.target.value)}
              className="w-10 h-10 rounded cursor-pointer border"
            />
            <Input
              value={thresholdEditData.color}
              onChange={(e) => onThresholdFieldChange('color', e.target.value)}
              placeholder="#22C55E"
              className="flex-1"
            />
          </div>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="flex-1 space-y-2">
          <Label htmlFor={`description-${threshold.id}`}>Description</Label>
          <Input
            id={`description-${threshold.id}`}
            value={thresholdEditData.description}
            onChange={(e) => onThresholdFieldChange('description', e.target.value)}
            placeholder="Description for this grade level"
          />
        </div>
        <div className="pt-6">
          <Button
            variant="destructive"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onDeleteThreshold(threshold.id, threshold.display_label || threshold.label || threshold.grade);
            }}
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Delete
          </Button>
        </div>
      </div>
    </div>
  );
}

// Threshold View Item Sub-component
interface ThresholdViewItemProps {
  threshold: SupplierRatingThreshold;
  onEditThreshold: (threshold: SupplierRatingThreshold) => void;
}

function ThresholdViewItem({
  threshold,
  onEditThreshold,
}: ThresholdViewItemProps) {
  return (
    <div
      className="flex items-center gap-4 p-4 border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
      style={{ borderLeftColor: threshold.effective_color || threshold.color, borderLeftWidth: '4px' }}
      onClick={() => onEditThreshold(threshold)}
    >
      <div
        className="w-12 h-12 rounded-lg flex items-center justify-center text-white text-xl font-bold"
        style={{ backgroundColor: threshold.effective_color || threshold.color }}
      >
        {threshold.grade}
      </div>
      <div className="flex-1">
        <div className="font-medium flex items-center gap-2">
          {threshold.display_label || threshold.label || `Grade ${threshold.grade}`}
          {(threshold.label || threshold.display_label) && threshold.label !== threshold.grade && (
            <Badge variant="secondary" className="text-xs">
              Grade {threshold.grade}
            </Badge>
          )}
        </div>
        <div className="text-sm text-muted-foreground">{threshold.description}</div>
      </div>
      <div className="text-right">
        <div className="font-medium">{threshold.min_score} - {threshold.max_score}</div>
        <div className="text-sm text-muted-foreground">Score Range</div>
      </div>
      <div className="flex items-center gap-2">
        {threshold.grade === 'A' && <CheckCircle className="h-5 w-5 text-green-500" />}
        {threshold.grade === 'F' && <AlertTriangle className="h-5 w-5 text-red-500" />}
        <Pencil className="h-4 w-4 text-muted-foreground" />
      </div>
    </div>
  );
}

// Add Threshold Form Sub-component
interface AddThresholdFormProps {
  // CR-7271: the form does not carry `performance_settings`; the hook supplies
  // it from the settings row being edited at the point of the POST.
  newThresholdData: Omit<CreateRatingThresholdData, 'performance_settings'>;
  addingThreshold: boolean;
  onSetShowAddThreshold: (show: boolean) => void;
  onAddThreshold: () => void;
  onNewThresholdFieldChange: (field: string, value: string | number) => void;
}

function AddThresholdForm({
  newThresholdData,
  addingThreshold,
  onSetShowAddThreshold,
  onAddThreshold,
  onNewThresholdFieldChange,
}: AddThresholdFormProps) {
  return (
    <div className="mt-4 p-4 border rounded-lg space-y-4 bg-muted/30">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className="w-12 h-12 rounded-lg flex items-center justify-center text-white text-xl font-bold"
            style={{ backgroundColor: newThresholdData.color }}
          >
            {newThresholdData.grade || '?'}
          </div>
          <div className="font-medium text-lg">Add New Grade</div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onSetShowAddThreshold(false)}
            disabled={addingThreshold}
          >
            <X className="h-4 w-4 mr-1" />
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={onAddThreshold}
            disabled={addingThreshold || !newThresholdData.grade.trim()}
          >
            {addingThreshold ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Plus className="h-4 w-4 mr-1" />
            )}
            Add Grade
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <div className="space-y-2">
          <Label htmlFor="new-grade">Grade ID *</Label>
          <Input
            id="new-grade"
            value={newThresholdData.grade}
            onChange={(e) => onNewThresholdFieldChange('grade', e.target.value)}
            placeholder="e.g., A+, B+, S"
            maxLength={10}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="new-label">Display Label</Label>
          <Input
            id="new-label"
            value={newThresholdData.label}
            onChange={(e) => onNewThresholdFieldChange('label', e.target.value)}
            placeholder="e.g., Platinum, Elite"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="new-min-score">Min Score</Label>
          <Input
            id="new-min-score"
            type="number"
            min="0"
            max="100"
            value={newThresholdData.min_score}
            onChange={(e) => onNewThresholdFieldChange('min_score', e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="new-max-score">Max Score</Label>
          <Input
            id="new-max-score"
            type="number"
            min="0"
            max="100"
            value={newThresholdData.max_score}
            onChange={(e) => onNewThresholdFieldChange('max_score', e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="new-color">Color</Label>
          <div className="flex items-center gap-2">
            <input
              id="new-color"
              type="color"
              value={newThresholdData.color}
              onChange={(e) => onNewThresholdFieldChange('color', e.target.value)}
              className="w-10 h-10 rounded cursor-pointer border"
            />
            <Input
              value={newThresholdData.color}
              onChange={(e) => onNewThresholdFieldChange('color', e.target.value)}
              placeholder="#3B82F6"
              className="flex-1"
            />
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="new-description">Description</Label>
        <Input
          id="new-description"
          value={newThresholdData.description}
          onChange={(e) => onNewThresholdFieldChange('description', e.target.value)}
          placeholder="Description for this grade level"
        />
      </div>
    </div>
  );
}
